import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDrop, useDrag } from 'react-dnd';
import { Document, Page, pdfjs } from 'react-pdf';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ZoomInIcon,
  ZoomOutIcon,
  PenLine,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

// --- Types ---

export interface PlacedSignatureField {
  id: string;
  x: number;
  y: number;
  pageNumber: number;
  width: number;
  height: number;
}

interface QuickSendCanvasProps {
  documentUrl: string;
  fields: PlacedSignatureField[];
  onFieldsChange: (fields: PlacedSignatureField[]) => void;
}

// --- Draggable signature chip (sidebar) ---

const SignatureChip: React.FC<{ label: string }> = ({ label }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'signature-field',
    item: { type: 'signature' },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }), []);

  const dragLatest = useRef(drag);
  dragLatest.current = drag;
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    dragLatest.current(node);
  }, []);

  return (
    <div
      ref={combinedRef}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-indigo-300 bg-indigo-50 cursor-grab select-none transition-all',
        'shadow-[0_0_0_0_rgba(99,102,241,0.4)] animate-[signature-pulse_2s_ease-in-out_infinite]',
        isDragging ? 'opacity-40 scale-95' : 'opacity-100 hover:scale-105 hover:shadow-lg'
      )}
    >
      <PenLine className="h-4 w-4 text-indigo-600 animate-[signature-wiggle_2.5s_ease-in-out_infinite]" />
      <span className="text-sm font-medium text-indigo-700">{label}</span>
    </div>
  );
};

// --- Placed field on canvas ---

interface PlacedFieldChipProps {
  field: PlacedSignatureField;
  onRemove: () => void;
}

const PlacedFieldChip: React.FC<PlacedFieldChipProps> = ({ field, onRemove }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'placed-signature',
    item: { id: field.id },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }), [field.id]);

  const dragLatest = useRef(drag);
  dragLatest.current = drag;
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    dragLatest.current(node);
  }, []);

  return (
    <div
      ref={combinedRef}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-indigo-300 bg-indigo-50/80 cursor-move select-none',
        isDragging ? 'opacity-40' : 'opacity-100'
      )}
      style={{ minWidth: 100, minHeight: 36 }}
    >
      <PenLine className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
      <span className="text-xs font-medium text-indigo-700">Signature</span>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="ml-auto text-red-400 hover:text-red-600"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// --- Error boundary ---

class PdfErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidUpdate(_: any, prev: { hasError: boolean }) {
    if (this.state.hasError && !prev.hasError) {
      setTimeout(() => this.setState({ hasError: false }), 0);
    }
  }
  render() {
    return this.state.hasError ? (this.props.fallback || null) : this.props.children;
  }
}

// --- Main component ---

const QuickSendCanvas: React.FC<QuickSendCanvasProps> = ({
  documentUrl,
  fields,
  onFieldsChange,
}) => {
  const { t } = useTranslation('quicksend');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  const pdfOptions = useMemo(() => ({}), []);

  const handleLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setLoading(false);
    setDocumentLoaded(true);
    setPageNumber(1);
    setError(null);
  };

  const handleLoadError = (err: Error) => {
    setError(err);
    setLoading(false);
  };

  // Add a new field
  const addField = useCallback((x: number, y: number) => {
    const newField: PlacedSignatureField = {
      id: `sig-${Date.now()}`,
      x,
      y,
      pageNumber,
      width: 100,
      height: 40,
    };
    onFieldsChange([...fields, newField]);
  }, [pageNumber, fields, onFieldsChange]);

  // Move an existing field
  const moveField = useCallback((id: string, x: number, y: number) => {
    onFieldsChange(fields.map(f => f.id === id ? { ...f, x, y } : f));
  }, [fields, onFieldsChange]);

  // Remove a field
  const removeField = useCallback((id: string) => {
    onFieldsChange(fields.filter(f => f.id !== id));
  }, [fields, onFieldsChange]);

  // Drop handler
  const [, drop] = useDrop(() => ({
    accept: ['signature-field', 'placed-signature'],
    drop: (item: any, monitor) => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const offset = monitor.getSourceClientOffset();
      if (!offset) return;

      const x = ((offset.x - rect.left) / rect.width) * 100;
      const y = ((offset.y - rect.top) / rect.height) * 100;

      if (monitor.getItemType() === 'placed-signature') {
        moveField(item.id, x, y);
      } else {
        addField(x, y);
      }
    },
  }), [addField, moveField]);

  const dropLatest = useRef(drop);
  dropLatest.current = drop;
  const canvasDropRef = useCallback((node: HTMLDivElement | null) => {
    canvasRef.current = node;
    dropLatest.current(node);
  }, []);

  // Touch tap-to-place fallback for mobile
  const handleCanvasTap = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Only act on direct canvas taps (not on placed fields), and only on touch devices
    if ((e.target as HTMLElement).closest('[data-placed-field]')) return;
    if (!('ontouchstart' in window)) return;
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    addField(x, y);
  }, [addField]);

  return (
    <div className="flex flex-col lg:flex-row gap-4 w-full">
      {/* Sidebar: draggable signature chip */}
      <div className="lg:w-48 flex lg:flex-col gap-3 items-start">
        <div>
          <p className="text-xs text-muted-foreground mb-2 hidden lg:block">
            {t('fields_instruction')}
          </p>
          <SignatureChip label={t('fields_signature')} />
        </div>

        <div className="mt-3 hidden lg:block">
          <a href="/join" className="text-xs font-bold text-primary hover:underline">
            {t('fields_signup_cta')}
          </a>
          <ul className="text-xs text-muted-foreground mt-1 list-disc list-inside space-y-0.5">
            <li>{t('fields_benefit_signers')}</li>
            <li>{t('fields_benefit_fields')}</li>
            <li>{t('fields_benefit_more')}</li>
          </ul>
        </div>
      </div>

      {/* Canvas area */}
      <div className="flex-1 flex flex-col items-center">
        {/* Controls */}
        <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setPageNumber(p => Math.max(p - 1, 1))} disabled={pageNumber <= 1}>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            {pageNumber} / {numPages || '?'}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPageNumber(p => Math.min(p + 1, numPages ?? p))} disabled={!numPages || pageNumber >= numPages}>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>

          <div className="border-l h-5 mx-1" />

          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setScale(s => Math.max(s - 0.1, 0.5))}>
            <ZoomOutIcon className="h-3.5 w-3.5" />
          </Button>
          <span className="text-xs w-10 text-center">{Math.round(scale * 100)}%</span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setScale(s => Math.min(s + 0.1, 2.0))}>
            <ZoomInIcon className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* PDF + fields */}
        <div
          ref={canvasDropRef}
          onClick={handleCanvasTap}
          className="relative bg-white shadow-md rounded-md mx-auto"
          style={{ width: documentLoaded ? 'fit-content' : `${scale * 100}%`, maxWidth: '98%' }}
        >
          {loading && <Skeleton className="h-[800px] w-full" />}
          {error && (
            <div className="p-8 border border-destructive/50 rounded-md text-center">
              <p className="text-destructive text-sm">Error loading document: {error.message}</p>
            </div>
          )}

          {documentUrl && !loading && !error && (
            <PdfErrorBoundary fallback={<Skeleton className="h-[800px] w-full" />}>
              <Document
                key={documentUrl}
                file={documentUrl}
                options={pdfOptions}
                onLoadSuccess={handleLoadSuccess}
                onLoadError={handleLoadError}
                loading={<Skeleton className="h-[800px] w-full" />}
              >
                {documentLoaded && (
                  <Page
                    key={`page_${pageNumber}_${scale}`}
                    pageNumber={pageNumber}
                    scale={scale}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    onRenderError={(err) => {
                      if (err.message !== 'Transport destroyed') setError(err);
                    }}
                    loading={<Skeleton className="h-[800px] w-full" />}
                  />
                )}
              </Document>
            </PdfErrorBoundary>
          )}

          {/* Placed fields overlay */}
          {fields
            .filter(f => f.pageNumber === pageNumber)
            .map(field => (
              <div
                key={field.id}
                data-placed-field
                className="absolute"
                style={{
                  left: `${field.x}%`,
                  top: `${field.y}%`,
                  zIndex: 10,
                }}
              >
                <PlacedFieldChip
                  field={field}
                  onRemove={() => removeField(field.id)}
                />
              </div>
            ))}
        </div>

        {/* Mobile instruction */}
        <p className="text-xs text-muted-foreground mt-2 text-center lg:hidden">
          {t('fields_instruction')}
        </p>
        <div className="mt-2 text-center lg:hidden">
          <a href="/join" className="text-xs font-bold text-primary hover:underline">
            {t('fields_signup_cta')}
          </a>
          <ul className="text-xs text-muted-foreground mt-1 list-disc list-inside space-y-0.5">
            <li>{t('fields_benefit_signers')}</li>
            <li>{t('fields_benefit_fields')}</li>
            <li>{t('fields_benefit_more')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QuickSendCanvas;
